import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { BusinessExpense } from '~/schemas/businessExpenses'
import { businessExpenseSchema } from '~/schemas/businessExpenses'

export interface GeneralExpense {
  id: number
  amount: number
  notes?: string
  expense_id: number
  fund_source_id?: number | null
  business_id: string
  shift_id: null
  created_at: string
  updated_at?: string
  expense_date?: string
  expense_type?: string
}

interface ExpenseType {
  value: number
  label: string
}

export const useGeneralExpensesStore = defineStore('generalExpenses', () => {
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const log = useLogger('store:generalExpenses')
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  const { getBrowserTimezone, formatDateForAPI } = useTimezone()

  // State
  const expenses: Ref<GeneralExpense[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const totalExpenses: Ref<number> = ref(0)
  const selectedDate: Ref<Date> = ref(new Date())

  // Modal states
  const showCreateModal: Ref<boolean> = ref(false)
  const showEditModal: Ref<boolean> = ref(false)
  const selectedExpense: Ref<GeneralExpense | null> = ref(null)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $confirm } = nuxtApp

  // Computed properties
  const formattedDate = computed(() => {
    return formatDateForAPI(selectedDate.value)
  })

  const totalDailyExpenses = computed(() => {
    return expenses.value?.reduce((sum, expense) => sum + (expense.amount || 0), 0) || 0
  })

  // Core data fetching
  async function fetchDailyExpenses(date?: Date): Promise<void> {
    if (!businessId.value) return

    const targetDate = date || selectedDate.value
    selectedDate.value = targetDate

    loading.value = true
    try {
      const timezone = getBrowserTimezone()
      const params = new URLSearchParams({
        date: formatDateForAPI(targetDate),
        timezone,
      })
      const res: any = await get(`/api/business/${businessId.value}/expenses/daily?${params}`)

      if (res?.success) {
        // Filter only general expenses (shift_id is null)
        const allExpenses = res.data || []
        expenses.value = allExpenses.filter((expense: any) => expense.shift_id === null)
        totalExpenses.value = expenses.value.length
      }
      else {
        log.error('Invalid API response:', res)
        useErrorToast(t('Failed to load expenses'))
      }
    }
    catch (error) {
      log.error('Error fetching daily expenses:', error)
      useErrorToast(t('Failed to load expenses'))
    }
    finally {
      loading.value = false
    }
  }

  // Expense types - no caching for admin forms
  async function fetchExpenseTypes(): Promise<ExpenseType[]> {
    if (!businessId.value) return []

    try {
      // Fetch fresh data every time for admin forms - no caching
      const response = await get(`/api/business/${businessId.value}/expenses/types`)
      // Use the select format (already filtered for active types only)
      return response?.select || []
    }
    catch (error) {
      log.error('Error fetching expense types:', error)
      return []
    }
  }

  // Fund sources - no caching for admin forms
  async function fetchFundSources(): Promise<ExpenseType[]> {
    if (!businessId.value) return []

    try {
      const response = await get(`/api/business/${businessId.value}/expenses/fund-sources`)
      // Use the select format (already filtered for active sources only)
      return response?.select || []
    }
    catch (error) {
      log.error('Error fetching fund sources:', error)
      return []
    }
  }

  // CRUD operations
  async function createExpense(formData: Partial<BusinessExpense>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast(t('Business ID not found'))
      return false
    }

    // Prepare data with required fields
    const data = {
      ...formData,
      expense_date: formData.expense_date ?? new Date(),
      business_id: businessId.value,
      // null = global expense; the create modal sets the open shift's id when
      // the operator asks to link it (see CreateModal).
      shift_id: formData.shift_id ?? null,
    }

    // Validate the complete data
    const validatedData = businessExpenseSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast(t('Validation failed. Please check the entered details and try again.'))
      return false
    }

    try {
      const response = await post(`/api/business/${businessId.value}/expenses`, validatedData.data)

      if (response?.success) {
        useSuccessToast(t('Expense created successfully'))
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Error creating expense:', error)
      useErrorToast(t('Failed to create expense'))
      return false
    }
  }

  async function updateExpense(expenseId: number, formData: Partial<BusinessExpense>): Promise<boolean> {
    if (!businessId.value) return false

    try {
      const response = await put(`/api/business/${businessId.value}/expenses/${expenseId}`, {
        amount: formData.amount,
        notes: formData.notes,
        expense_id: formData.expense_id,
        fund_source_id: formData.fund_source_id,
        expense_date: formData.expense_date,
      })

      if (response?.success) {
        useSuccessToast(t('Expense updated successfully'))
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Error updating expense:', error)
      useErrorToast(t('Failed to update expense'))
      return false
    }
  }

  async function deleteExpense(expense: GeneralExpense): Promise<void> {
    if (!businessId.value) return

    log.info('Deleting expense:', expense)
    try {
      const url = `/api/business/${businessId.value}/expenses/${expense.id}`
      log.info('Delete URL:', url)
      await del(url)
      useSuccessToast(t('Expense deleted successfully'))
    }
    catch (error: any) {
      log.error('Error deleting expense:', error)
      log.error('Error details:', JSON.stringify(error, null, 2))
      useErrorToast(t('Failed to delete expense'))
      throw error
    }
  }

  async function deleteExpenseWithConfirm(expense: GeneralExpense): Promise<void> {
    if (!businessId.value) return

    $confirm.require({
      group: 'inline',
      message: t('Do you want to delete this record?'),
      icon: 'pi pi-info-circle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        await deleteExpense(expense)
      },
      reject: () => {
        // User cancelled deletion
      },
    })
  }

  async function getExpense(expenseId: number): Promise<GeneralExpense | null> {
    if (!businessId.value) return null

    try {
      const response = await get(`/api/business/${businessId.value}/expense/${expenseId}`)
      return response?.data || null
    }
    catch (error) {
      log.error('Error fetching expense:', error)
      return null
    }
  }

  // Modal methods
  function openCreateModal(): void {
    showCreateModal.value = true
  }

  function closeCreateModal(): void {
    showCreateModal.value = false
  }

  function openEditModal(expense: GeneralExpense): void {
    selectedExpense.value = expense
    showEditModal.value = true
  }

  function closeEditModal(): void {
    selectedExpense.value = null
    showEditModal.value = false
  }

  // Date management
  function setSelectedDate(date: Date): void {
    selectedDate.value = date
    fetchDailyExpenses(date)
  }

  // Reset method to clear all state
  function reset(): void {
    expenses.value = []
    loading.value = false
    totalExpenses.value = 0
    selectedDate.value = new Date()
    showCreateModal.value = false
    showEditModal.value = false
    selectedExpense.value = null
  }

  return {
    // State
    expenses,
    loading,
    totalExpenses,
    selectedDate,
    showCreateModal,
    showEditModal,
    selectedExpense,

    // Computed
    formattedDate,
    totalDailyExpenses,

    // Core methods
    fetchDailyExpenses,
    fetchExpenseTypes,
    fetchFundSources,
    createExpense,
    updateExpense,
    deleteExpense,
    deleteExpenseWithConfirm,
    getExpense,

    // Modal methods
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,

    // Date methods
    setSelectedDate,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'general-expenses',
    storage: localStorage,
    paths: ['selectedDate'],
  },
})
